import { useState, useRef } from "react";

import parseMarkdown from "../utils/parseMarkdown";

// Reusable markdown editor with formatting toolbar, preview toggle, link dialog.
// Shared by ticket replies, comment edits, and the new-ticket modal.
const MarkdownEditor = ({
  value,
  onChange,
  placeholder = "Write something... (Markdown formatting supported)",
  minHeightClass = "min-h-32",
  textareaRef: externalRef,
  rightSlot,
  disabled = false,
}) => {
  const internalRef = useRef(null);
  const ref = externalRef || internalRef;
  const [showPreview, setShowPreview] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  const insertFormatting = (before, after = "") => {
    if (disabled) return;

    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);
    const next =
      value.substring(0, start) +
      before +
      selected +
      after +
      value.substring(end);

    onChange(next);

    setTimeout(() => {
      ta.focus();
      const pos = start + before.length + selected.length;

      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleBold = () => insertFormatting("**", "**");
  const handleItalic = () => insertFormatting("*", "*");
  const handleUnderline = () => insertFormatting("<u>", "</u>");
  const handleStrikethrough = () => insertFormatting("~~", "~~");
  const handleCode = () => insertFormatting("`", "`");

  const handleCodeBlock = () => {
    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);

    if (selected) {
      insertFormatting("```\n", "\n```");
    } else {
      const codeBlock = "```javascript\n// Your code here\n```";
      const next = value.substring(0, start) + codeBlock + value.substring(end);

      onChange(next);
      setTimeout(() => {
        ta.focus();
        const newPos = start + 14;

        ta.setSelectionRange(newPos, newPos + 17);
      }, 0);
    }
  };

  const handleQuote = () => {
    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);

    if (selected) {
      const quoted = selected
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
      const next = value.substring(0, start) + quoted + value.substring(end);

      onChange(next);

      setTimeout(() => {
        ta.focus();
        ta.setSelectionRange(start, start + quoted.length);
      }, 0);
    } else {
      insertFormatting("> ", "");
    }
  };

  const handleBulletList = () => {
    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);

    if (selected) {
      const listText = selected
        .split("\n")
        .map((line) => `- ${line}`)
        .join("\n");
      const next = value.substring(0, start) + listText + value.substring(end);

      onChange(next);
      setTimeout(() => {
        ta.focus();
        ta.setSelectionRange(start, start + listText.length);
      }, 0);
    } else {
      insertFormatting("- ", "");
    }
  };

  const handleNumberedList = () => {
    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);

    if (selected) {
      const lines = selected.split("\n");
      const listText = lines
        .map((line, idx) => `${idx + 1}. ${line}`)
        .join("\n");
      const next = value.substring(0, start) + listText + value.substring(end);

      onChange(next);
      setTimeout(() => {
        ta.focus();
        ta.setSelectionRange(start, start + listText.length);
      }, 0);
    } else {
      insertFormatting("1. ", "");
    }
  };

  const handleCheckbox = () => insertFormatting("- [ ] ", "");
  const handleHeading1 = () => insertFormatting("# ", "");
  const handleHeading2 = () => insertFormatting("## ", "");
  const handleHeading3 = () => insertFormatting("### ", "");

  const handleHorizontalRule = () => {
    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const next = `${value.substring(0, start)}\n---\n${value.substring(start)}`;

    onChange(next);
    setTimeout(() => {
      ta.focus();
      const newPos = start + 5;

      ta.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleTable = () => {
    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const tableTemplate = `\n| Header 1 | Header 2 | Header 3 |\n|----------|----------|----------|\n| Cell 1   | Cell 2   | Cell 3   |\n| Cell 4   | Cell 5   | Cell 6   |\n`;
    const next =
      value.substring(0, start) + tableTemplate + value.substring(start);

    onChange(next);
    setTimeout(() => {
      ta.focus();
      const headerStart = start + 3;

      ta.setSelectionRange(headerStart, headerStart + 8);
    }, 0);
  };

  const handleLink = () => {
    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);

    setLinkText(selected);
    setShowLinkDialog(true);
  };

  const insertLink = () => {
    const ta = ref.current;

    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const linkMarkdown = `[${linkText || "Link"}](${linkUrl})`;
    const next =
      value.substring(0, start) + linkMarkdown + value.substring(end);

    onChange(next);
    setShowLinkDialog(false);
    setLinkUrl("");
    setLinkText("");
    setTimeout(() => {
      ta.focus();
    }, 0);
  };

  const toolbarBtnClass = (active) =>
    `p-2 text-gray-600 dark:text-gray-400 rounded transition-colors ${
      active || disabled
        ? "cursor-not-allowed opacity-60"
        : "hover:bg-gray-200 dark:hover:bg-gray-700"
    }`;

  return (
    <>
      {/* Formatting Toolbar */}
      <div
        className={`flex flex-wrap items-center gap-1 p-2 bg-gray-50 dark:bg-gray-800 rounded-t-lg border border-gray-200 dark:border-gray-700 border-b-0 ${
          showPreview ? "opacity-50" : ""
        }`}
      >
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Bold (Ctrl+B)"
          type="button"
          onClick={handleBold}
        >
          <i className="fas fa-bold" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Italic (Ctrl+I)"
          type="button"
          onClick={handleItalic}
        >
          <i className="fas fa-italic" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Underline (Ctrl+U)"
          type="button"
          onClick={handleUnderline}
        >
          <i className="fas fa-underline" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Strikethrough"
          type="button"
          onClick={handleStrikethrough}
        >
          <i className="fas fa-strikethrough" />
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        {/* Headers */}
        <div className="relative group">
          <button
            className={`p-2 text-gray-600 dark:text-gray-400 rounded transition-colors flex items-center gap-1 ${
              showPreview || disabled
                ? "cursor-not-allowed opacity-60"
                : "hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
            disabled={showPreview || disabled}
            title="Headers"
            type="button"
          >
            <i className="fas fa-heading" />
            <i className="fas fa-chevron-down text-xs" />
          </button>
          {!showPreview && !disabled && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg hidden group-hover:block z-10">
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
                type="button"
                onClick={handleHeading1}
              >
                <span className="text-xl font-bold">H1</span>
              </button>
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                type="button"
                onClick={handleHeading2}
              >
                <span className="text-lg font-bold">H2</span>
              </button>
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                type="button"
                onClick={handleHeading3}
              >
                <span className="text-base font-bold">H3</span>
              </button>
            </div>
          )}
        </div>

        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Quote"
          type="button"
          onClick={handleQuote}
        >
          <i className="fas fa-quote-left" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Inline Code"
          type="button"
          onClick={handleCode}
        >
          <i className="fas fa-code" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Code Block"
          type="button"
          onClick={handleCodeBlock}
        >
          <i className="fas fa-file-code" />
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Bullet List"
          type="button"
          onClick={handleBulletList}
        >
          <i className="fas fa-list-ul" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Numbered List"
          type="button"
          onClick={handleNumberedList}
        >
          <i className="fas fa-list-ol" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Checkbox"
          type="button"
          onClick={handleCheckbox}
        >
          <i className="fas fa-check-square" />
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Insert Link (Ctrl+K)"
          type="button"
          onClick={handleLink}
        >
          <i className="fas fa-link" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Insert Table"
          type="button"
          onClick={handleTable}
        >
          <i className="fas fa-table" />
        </button>
        <button
          className={toolbarBtnClass(showPreview)}
          disabled={showPreview || disabled}
          title="Horizontal Rule"
          type="button"
          onClick={handleHorizontalRule}
        >
          <i className="fas fa-minus" />
        </button>

        <div className="ml-auto flex items-center gap-2">
          {typeof rightSlot === "function"
            ? rightSlot({ showPreview })
            : rightSlot}
          <button
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              showPreview
                ? "bg-primary-600 text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
            type="button"
            onClick={() => setShowPreview(!showPreview)}
          >
            <i className={`fas fa-${showPreview ? "edit" : "eye"} mr-1`} />
            {showPreview ? "Edit" : "Preview"}
          </button>
        </div>
      </div>

      {/* Edit/Preview Area */}
      {!showPreview ? (
        <textarea
          autoCapitalize="sentences"
          autoCorrect="on"
          className={`input w-full ${minHeightClass} resize-y rounded-t-none border-t-0 font-mono text-sm`}
          disabled={disabled}
          placeholder={placeholder}
          ref={ref}
          spellCheck="true"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.ctrlKey || e.metaKey) {
              switch (e.key) {
                case "b":
                  e.preventDefault();
                  handleBold();
                  break;
                case "i":
                  e.preventDefault();
                  handleItalic();
                  break;
                case "u":
                  e.preventDefault();
                  handleUnderline();
                  break;
                case "k":
                  e.preventDefault();
                  handleLink();
                  break;
                default:
                  break;
              }
            }
          }}
        />
      ) : (
        <div className="min-h-[8rem] p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-b-lg border-t-0">
          {value.trim() ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div
                dangerouslySetInnerHTML={{
                  __html: parseMarkdown(value),
                }}
              />
            </div>
          ) : (
            <p className="text-gray-400 italic">Nothing to preview</p>
          )}
        </div>
      )}

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="font-semibold mb-4">Insert Link</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Link Text
                </label>
                <input
                  autoFocus
                  className="input w-full"
                  placeholder="Enter link text"
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">URL</label>
                <input
                  className="input w-full"
                  placeholder="https://example.com"
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowLinkDialog(false);
                  setLinkUrl("");
                  setLinkText("");
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={insertLink}>
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarkdownEditor;
